import React, { useEffect, useState, useRef, useCallback } from "react"
import * as Sentry from "@sentry/react"
import { useAtom } from "jotai"
import { useNavigate } from "react-router-dom"
import { isEmpty } from "lodash"
import { Socket } from "phoenix"
import {
  handleApiError,
  showErrorAlert,
  reportError,
} from "../../helpers/errorHandler"
import {
  isSingleTrip,
  isRoundTripDeparture,
  isRoundTripReturn,
  filterFares,
} from "../../helpers/tripHelpers"
import {
  buildBlockPayload,
  buildUnblockPayload,
} from "../../helpers/seatBlockPayload"
import { pushSeatSelected, pushSeatUnselected } from "../../helpers/seatmapPush"
import { createSeatLockTracker } from "../../helpers/seatLockTracker"
import {
  resolvePassengerSyncEntryId,
  findPassengerSeat,
} from "../../helpers/seatPassengerSync"
import {
  executeSeatTapSelect,
  executeOptimisticContinuar,
  isIgnorableBlock404,
} from "../../helpers/seatOptimisticSelect"
import { isFlightRecorderEnabled, recordFlightEvent } from "../../debug/flightRecorder"
import { beginFlowSpan, getCurrentFlowSpanId } from "../../debug/flightCorrelation"
import {
  shouldPaintOccupied,
  remoteOccupiedSeatIds,
  applySeatTypeOverrides,
  verifyCountFares,
} from "../../helpers/seatOwnership"
import { differenceInSeconds, parseISO } from "date-fns" // <-- agregado
import { handleBookingTimeout } from "../../helpers/bookingTimer"

import { BASE_URL } from "../../utilities/constants"
import transpais from "../../services/transpais"
import PassengerView from "./partials/PassengerView"

import {
  bookingSessionDepartureDisponibilidad,
  bookingSessionDepartureTrip,
  bookingSessionReturningTrip,
  bookingSessionDestination,
  bookingSessionOrigin,
  bookingSessionDepartureBlockedTickets,
  bookingSessionTripConfig,
  bookingSessionReturningDisponibilidad,
  bookingSessionFares,
  bookingSessionCountFares,
  TranspaisIDToken,
  bookingSessionTrips,
  bookingSessionPassengers,
  bookingSessionTimeoutDeadline, // <-- agregado
} from "../../utilities/atoms"

import { useModal } from "../../contexts/modal.context"
import { useModalView } from "../../contexts/modalview.context"
import { useTimer } from "../../contexts/timer.context" // <-- agregado

import {
  Button,
  Timer,
  TripData,
  Acotaciones,
  BusLayout,
  Loader,
} from "../../components/transpais"
import styles from "./BookingBus.module.scss"
import global from "./BookingGlobal.module.scss"

const MAX_RETRY_ATTEMPTS = 5
const RETRY_DELAYS = [1000, 2000, 5000, 10000, 15000]

const useBusData = () => {
  const [bus, setBus] = useState([])
  const [selected, setSelected] = useAtom(bookingSessionPassengers)
  const [sectionsIds, setSectionsIds] = useState([])
  const [localLoading, setLocalLoading] = useState(false)

  const processBusLayout = (sections) => {
    return sections.map((section) => ({
      ...section,
      rows: section.rows.map((row) => ({
        ...row,
        seats: row.seats.map((seat) => ({
          ...seat,
          isWindowSeat: seat.col === 1 || seat.col === row.seats.length, // Ventanilla: primera o última columna
          isCorridor: seat.type === "facilities" && seat.code === "corridor", // Identificar pasillo
        })),
      })),
    }))
  }

  const fetchBusData = async (
    session,
    trip,
    origin,
    destination,
    isRoundTrip,
  ) => {
    let typeTrip = isRoundTrip ? "RETURN" : "DEPARTURE"
    console.log("session, trip, origin, destination, isRoundTrip: ", {
      session,
      trip,
      origin,
      destination,
      isRoundTrip,
    })
    console.log("fetchBusData: " + typeTrip, {
      step: isRoundTrip ? "return" : "departure",
      trip_id: trip.id,
      origin: origin._id,
      destination: destination._id,
      arrival: trip.arrival,
      departure: trip.departure,
      duration: trip.duration,
      route: trip.segments[0].routeId,
      schedule: trip.segments[0].name,
      seat_map: trip.segments[0].seatMapId,
      manifest: trip.segments[0].manifestDate,
      manifest_id: trip.segments[0].manifestId,
      leg_from: trip.segments[0].legFrom,
      leg_to: trip.segments[0].legTo,
    })
    setLocalLoading(true)
    try {
      const response = await transpais.bus(session, {
        step: isRoundTrip ? "return" : "departure",
        trip_id: trip.id,
        origin: origin._id,
        destination: destination._id,
        arrival: trip.arrival,
        departure: trip.departure,
        duration: trip.duration,
        route: trip.segments[0].routeId,
        schedule: trip.segments[0].name,
        seat_map: trip.segments[0].seatMapId,
        manifest: trip.segments[0].manifestDate,
        manifest_id: trip.segments[0].manifestId,
        leg_from: trip.segments[0].legFrom,
        leg_to: trip.segments[0].legTo,
      })
      console.log("RESPONSE BUS: ", response)
      const sections =
        response?.data?.data?.seatmap?.sections ??
        response?.data?.seatmap?.sections
      if (!Array.isArray(sections)) {
        throw new Error("Seatmap response missing data.seatmap.sections")
      }
      const processedBus = processBusLayout(sections)
      console.log("processedBus: ", processedBus)
      setBus(processedBus)
      setSectionsIds(processedBus)
    } catch (error) {
      console.error("Error fetching bus data:", error)
      Sentry.captureException(error)
    } finally {
      setLocalLoading(false)
    }
  }

  return {
    bus,
    selected,
    sectionsIds,
    localLoading,
    setBus,
    fetchBusData,
    setSelected,
    setLocalLoading,
  }
}

const BookingBus = () => {
  const navigate = useNavigate()
  const { showModalView, setupModalView, destroyModalView } = useModalView()
  const { showModal, hideModal, setupModal } = useModal()

  const [bookingOrigin] = useAtom(bookingSessionOrigin)
  const [bookingDestination] = useAtom(bookingSessionDestination)
  const [bookingDepartureTrip, setBookingDepartureTrip] = useAtom(
    bookingSessionDepartureTrip,
  )
  const [bookingReturningTrip, setBookingReturningTrip] = useAtom(
    bookingSessionReturningTrip,
  )
  const [bookingReturningDisponibilidad] = useAtom(
    bookingSessionReturningDisponibilidad,
  )
  const [bookingDepartureDisponibilidad] = useAtom(
    bookingSessionDepartureDisponibilidad,
  )
  const [bookingDepartureBlockedTickets, setBookingDepartureBlockedTickets] =
    useAtom(bookingSessionDepartureBlockedTickets)
  const [bookingTripConfig] = useAtom(bookingSessionTripConfig)
  const [countFares] = useAtom(bookingSessionCountFares)
  const [transpaisIdToken] = useAtom(TranspaisIDToken)
  const [session] = useAtom(bookingSessionTrips)
  const [fareList] = useAtom(bookingSessionFares)
  const [accessTicket, setAccessTicket] = useState(null)
  const [syncSeats, setSyncSeats] = useState([])
  const [bookingTimeoutDeadline, setBookingTimeoutDeadline] = useAtom(
    bookingSessionTimeoutDeadline,
  )
  const { hideTimer } = useTimer()
  const didRunTimeout = useRef(false) // <-- agregado
  const socketRef = useRef(null)
  const channelRef = useRef(null)
  const lockTrackerRef = useRef(createSeatLockTracker())
  const [retryCount, setRetryCount] = useState(0)
  const [isSocketConnected, setIsSocketConnected] = useState(false)

  const {
    bus,
    selected,
    sectionsIds,
    localLoading,
    setBus,
    fetchBusData,
    setSelected,
    setLocalLoading,
  } = useBusData()

  const [selectedFloor, setSelectedFloor] = useState(0)
  const [disabled, setDisabled] = useState(true)
  const currentSelected = useRef(selected)
  const [filteredFares, setFilteredFares] = useState(
    filterFares(fareList, countFares, selected),
  )
  /** seatId → type overrides from WS/sync (never mix with own selected seats) */
  const [remoteSeatTypes, setRemoteSeatTypes] = useState({})
  const baseBusRef = useRef([])
  // const reconnectSocket = useCallback(() => {
  //   if (retryCount >= MAX_RETRY_ATTEMPTS) {
  //     console.error('Max retry attempts reached. Giving up on socket connection.')
  //     //alert('No se pudo conectar al servidor. Por favor, inténtalo de nuevo más tarde.')
  //     return
  //   }
  //
  //   const delay = RETRY_DELAYS[retryCount] || RETRY_DELAYS[RETRY_DELAYS.length - 1]
  //   console.log(`Attempting to reconnect socket in ${delay}ms (attempt ${retryCount + 1}/${MAX_RETRY_ATTEMPTS})`)
  //
  //   setTimeout(() => {
  //     if (socketRef.current) {
  //       socketRef.current.disconnect()
  //       socketRef.current.connect()
  //       setRetryCount(prev => prev + 1)
  //     }
  //   }, delay)
  // }, [retryCount])

  const ownershipContext = () => ({
    selected: currentSelected.current,
    pendingSeatId: lockTrackerRef.current.getSeat()?.seatId ?? null,
  })

  const updateSeatStatus = (seatId, newType) => {
    setRemoteSeatTypes((prev) => ({ ...prev, [seatId]: newType }))
  }

  const clearRemoteSeatType = (seatId) => {
    setRemoteSeatTypes((prev) => {
      if (prev[seatId] == null) return prev
      const next = { ...prev }
      delete next[seatId]
      return next
    })
  }

  useEffect(() => {
    if (Array.isArray(sectionsIds) && sectionsIds.length > 0) {
      baseBusRef.current = sectionsIds
    }
    if (!baseBusRef.current.length) return
    setBus(applySeatTypeOverrides(baseBusRef.current, remoteSeatTypes))
  }, [sectionsIds, remoteSeatTypes, setBus])

  const getSegmentForSeatType = (seatType) => {
    const isReturn = seatType === "return"
    return isReturn
      ? bookingReturningTrip?.segments?.[0]
      : bookingDepartureTrip?.segments?.[0]
  }

  const softReleaseSeatHttp = async (seat, fareId, syncEntryId) => {
    try {
      await transpais.unblockSeat(
        session,
        buildUnblockPayload(seat, fareId, syncEntryId),
      )
    } catch (error) {
      console.warn("[BookingBus] unblockSeat failed (best-effort)", error)
      reportError(error, { action: "unblockSeat", seatId: seat?.seatId })
    }
  }

  const isFakeSeatBlockEnabled = () =>
    String(process.env.REACT_APP_FAKE_SEAT_BLOCK || "").trim() === "1"

  /** X on the passenger modal: WS unselect only — no /block was sent on tap. */
  const releasePendingSeatSelection = () => {
    const tracker = lockTrackerRef.current
    const pendingSeat = tracker.getSeat()
    if (!pendingSeat) return
    const segment = getSegmentForSeatType(pendingSeat.type)
    if (tracker.isWsSelected() && segment && channelRef.current) {
      pushSeatUnselected(channelRef.current, pendingSeat, segment)
    }
    tracker.reset()
  }

  const rollbackContinuarSelection = (seat, fareId) => {
    const seatId = seat?.seatId
    const updatedSelected = currentSelected.current
      .map((passenger) => {
        const seats = passenger.seats.filter(
          (s) =>
            !(
              String(s.seatId) === String(seatId) &&
              s.type === seat.type
            ),
        )
        if (seats.length === 0) return null
        return { ...passenger, seats }
      })
      .filter(Boolean)
    currentSelected.current = updatedSelected
    setSelected(updatedSelected)

    const segment = getSegmentForSeatType(seat.type)
    if (segment && channelRef.current) {
      pushSeatUnselected(channelRef.current, seat, segment)
    }
    void softReleaseSeatHttp(seat, fareId ?? null, seat.syncEntryId ?? null)
    if (lockTrackerRef.current.isTracking(seatId)) {
      lockTrackerRef.current.reset()
    }
  }

  /**
   * Single POST /block per confirmed seat: always fareId + kiosk-generated
   * syncEntryId (same UUID on both legs of a round trip).
   */
  const blockSeatWithFare = async (seat, fareId, syncEntryId) => {
    try {
      await transpais.blockSeat(
        session,
        buildBlockPayload(seat, fareId, syncEntryId),
      )
      return { ok: true, syncEntryId }
    } catch (error) {
      if (isIgnorableBlock404(error, isFakeSeatBlockEnabled())) {
        console.warn("[BookingBus] blockSeat 404 — continuing with WS only")
        return { ok: true }
      }
      const { userMessage, userTitle, errorData } = handleApiError(error, {
        action: "blockSeat",
        seatId: seat?.seatId,
        fareId,
      })
      showErrorAlert(userMessage, { setupModal, showModal, hideModal }, {
        title: userTitle,
      })
      reportError(error, errorData)
      return { ok: false }
    }
  }

  const handleSelectSeat = (seat, useChannel = true) => {
    const isSelectingReturn = isRoundTripReturn(
      bookingTripConfig,
      bookingReturningTrip,
    )

    // Crear el objeto del asiento seleccionado
    const _seat = {
      type: isSelectingReturn ? "return" : "departure",
      sectionId: seat.sectionId,
      rowLabel: seat.itemRow.toString(),
      seatNumber: seat.label.toString(),
      seatId: seat.id,
      seatClass: seat.seatClassId,
      col: seat.col,
      row: seat.row,
    }

    console.log("Crear el objeto del asiento seleccionado", _seat)

    if (useChannel && channelRef.current) {
      handleSeatSelection(_seat, seat.col)
      console.log("Conexión de socket disponible")
      console.log("Asiento seleccionado_:", seat)
    } else {
      console.warn("Conexión de socket no disponible o deshabilitada")
      //alert('Conexión de socket no disponible o deshabilitada. Por favor, inténtalo de nuevo más tarde.')
      // Intentar reconexión
      //reconnectSocket()
      handleSeatSelection(_seat, seat.col)
    }
  }

  const isWindowSeat = (seatCol) => {
    return seatCol === 1 || seatCol === 5 ? true : false
  }

  const findAdjacentSeat = (selectedSeat) => {
    if (!selectedSeat || !bus[selectedFloor]) return null
    // 1. Encontrar la fila correspondiente al asiento seleccionado
    const currentRow = bus[selectedFloor].rows.find(
      (row) => row.label == parseInt(selectedSeat.rowLabel, 10),
    )

    if (!currentRow) return null

    // 2. Buscar el asiento seleccionado en la fila
    const selectedSeatInRow = currentRow.seats.find(
      (seat) => seat.label === selectedSeat.seatNumber,
    )

    if (!selectedSeatInRow) return null

    //TODO: Proxima validacion revisar si el asiento ya fue seleccionado por uno de nuestros pasajeros retornar para validacion cuando sea mascota en caso de que el asiento adjacente no sea ventanilla
    // 3. Buscar el asiento contiguo en la misma fila
    const adjacentSeat = currentRow.seats.find((seat) => {
      // Verificar que sea un asiento normal (no pasillo)
      const isRegularSeat = seat.type === "open"
      // Verificar columna adyacente (±1)
      const isAdjacent = Math.abs(seat.col - selectedSeatInRow.col) === 1
      // No es el mismo asiento
      const isNotSameSeat = seat.id !== selectedSeatInRow.id

      return isRegularSeat && isAdjacent && isNotSameSeat
    })

    return adjacentSeat || null
  }

  // Método auxiliar para manejar la selección del asiento
  const handleSeatSelection = (_seat, col = null) => {
    // Re-tap while passenger modal pending: ignore (only X cancels)
    if (lockTrackerRef.current.isTracking(_seat.seatId)) {
      return
    }

    const isSelectingReturn = _seat.type === "return"

    // Filtrar pasajeros según el tipo de viaje
    const filteredSelected = selected.filter((passenger) =>
      passenger.seats.some(
        (seat) => seat.type === (isSelectingReturn ? "return" : "departure"),
      ),
    )

    // Obtener fares disponibles
    const filteredFares = filterFares(fareList, countFares, filteredSelected)

    // Verificar si el asiento ya está seleccionado localmente
    const passengerIndex = selected.findIndex((passenger) =>
      passenger.seats.some(
        (s) => s.seatId === _seat.seatId && s.type === _seat.type,
      ),
    )

    if (passengerIndex !== -1) {
      console.log("El asiento ya está seleccionado localmente", passengerIndex)
      // Lógica para deseleccionar el asiento
      const updatedSelected = [...selected]
      const passenger = updatedSelected[passengerIndex]

      if (_seat.type === "return") {
        passenger.seats = passenger.seats.filter((s) => s.type !== "return")
        if (passenger.seats.length === 0) {
          updatedSelected.splice(passengerIndex, 1)
        } else {
          updatedSelected[passengerIndex] = passenger
        }
      } else {
        updatedSelected.splice(passengerIndex, 1)
      }

      const segment = isSelectingReturn
        ? bookingReturningTrip.segments[0]
        : bookingDepartureTrip.segments[0]
      const existing = selected[passengerIndex]
      const fareIdForUnblock = existing?.fareId ?? null
      if (channelRef.current && segment) {
        pushSeatUnselected(channelRef.current, _seat, segment)
      }
      void softReleaseSeatHttp(
        {
          ..._seat,
          syncEntryId:
            findPassengerSeat(existing, _seat)?.syncEntryId ??
            existing?.syncEntryId ??
            _seat.syncEntryId,
        },
        fareIdForUnblock,
      )
      clearRemoteSeatType(_seat.seatId)
      currentSelected.current = updatedSelected
      setSelected(updatedSelected)
      return
    }

    const isWindow = isWindowSeat(col)
    const hasPetFare = filteredFares.some((fare) => fare.name === "Mascota")
    const petFare = filteredFares.find((fare) => fare.name === "Mascota")

    if (isEmpty(filteredFares)) {
      setupModal({
        title: "Atención",
        content: (
          <p>
            Ya han sido seleccionados todos los asientos para los pasajeros. Si
            deseas cambiar un asiento, primero debes deseleccionar uno de los
            asientos que ya están seleccionados.
          </p>
        ),
        actions: {
          positive: {
            title: "Ok",
            onClick: () => hideModal(),
          },
        },
      })
      showModal()
      return
    }
    // Si el asiento es de ventanilla
    if (isWindow && hasPetFare && findAdjacentSeat(_seat)) {
      console.log(
        "Asiento de ventanilla seleccionado. Mostrar solo fare Mascota.",
      )
      openPassengerModalForSeat(_seat, {
        title: "Registrar Mascota",
        fareList: [petFare],
      })
      return
    }

    openPassengerModalForSeat(_seat, {
      title:
        _seat.type === "return"
          ? "Selecciona el pasajero para el regreso"
          : "Selecciona el pasajero para la ida",
      fareList: filteredFares,
    })
  }

  const openPassengerModalForSeat = (
    _seat,
    { title, fareList: modalFares },
  ) => {
    const segment = getSegmentForSeatType(_seat.type)
    const flowSpanId = beginFlowSpan("seat")
    if (isFlightRecorderEnabled()) {
      recordFlightEvent(
        "SEAT:OPT_SELECT_TAP",
        {
          flowSpanId,
          seatId: _seat.seatId,
          label: _seat.label ?? _seat.rowLabel,
          type: _seat.type,
        },
        "BookingBus.openPassengerModalForSeat",
      )
    }

    executeSeatTapSelect({
      tracker: lockTrackerRef.current,
      seat: _seat,
      pushWsSelected: () => {
        if (segment && channelRef.current) {
          pushSeatSelected(channelRef.current, _seat, segment)
          lockTrackerRef.current.markWsSelected()
        }
      },
      openModal: () => {
        setupModalView({
          title,
          onCancel: () => {
            releasePendingSeatSelection()
          },
          content: (
            <PassengerView
              seat={_seat}
              selected={selected}
              onFinish={(passenger) => handlePassengerUpdate(passenger, _seat)}
              fareList={modalFares}
              countFares={countFares}
            />
          ),
        })
        showModalView()
      },
    })
  }

  const handlePassengerUpdate = (passenger, seat) => {
    console.log("handlePassengerUpdate", passenger, seat)
    if (isFlightRecorderEnabled()) {
      recordFlightEvent(
        "BOOKING:PASSENGER_CONFIRM",
        {
          flowSpanId: getCurrentFlowSpanId() || undefined,
          seatId: seat?.seatId,
          fareId: passenger?.fareId,
        },
        "BookingBus.handlePassengerUpdate",
      )
    }
    const isSelectingReturn = seat.type === "return"
    const fareClassIds = {
      outbound: bookingDepartureTrip.fareClasses[0].id,
      return: isSingleTrip(bookingTripConfig)
        ? bookingDepartureTrip.fareClasses[0].id
        : (bookingReturningTrip?.fareClasses?.[0]?.id ??
          bookingDepartureTrip.fareClasses[0].id),
    }

    const tracker = lockTrackerRef.current
    const passengerIndex = selected.findIndex(
      (p) =>
        p.firstName === passenger.firstName && p.lastName === passenger.lastName,
    )
    const matchedPassenger =
      passengerIndex >= 0 ? selected[passengerIndex] : null
    // One UUID per passenger: minted here for a new passenger, reused as-is when
    // the return leg picks a passenger that already has one.
    const syncEntryId = resolvePassengerSyncEntryId(passenger, matchedPassenger)
    const seatWithSync = { ...seat, syncEntryId }
    const fareId = passenger.fareId ?? matchedPassenger?.fareId ?? null
    const needsFareBlock = !!fareId

    const buildUpdatedSelected = () => {
      const updatedSelected = [...selected]

      if (passengerIndex >= 0) {
        const existingPassenger = updatedSelected[passengerIndex]
        if (isSelectingReturn) {
          const existingReturnSeat = existingPassenger.seats.find(
            (s) => s.type === "return",
          )
          if (existingReturnSeat) {
            existingPassenger.seats = existingPassenger.seats.map((s) =>
              s.type === "return" ? seatWithSync : s,
            )
          } else {
            existingPassenger.seats.push(seatWithSync)
          }
        } else {
          existingPassenger.seats = [seatWithSync]
        }
        updatedSelected[passengerIndex] = {
          ...existingPassenger,
          syncEntryId,
          ...(!isSelectingReturn
            ? {
                firstName: passenger.firstName ?? existingPassenger.firstName,
                lastName: passenger.lastName ?? existingPassenger.lastName,
                email: passenger.email ?? existingPassenger.email,
                fareId: passenger.fareId ?? existingPassenger.fareId,
                fare: passenger.fare ?? existingPassenger.fare,
              }
            : {}),
          fareClassIds,
          ...(!isSingleTrip(bookingTripConfig) && { items: [] }),
        }
      } else {
        updatedSelected.push({
          ...passenger,
          syncEntryId,
          seats: [seatWithSync],
          fareClassIds,
          ...(!isSingleTrip(bookingTripConfig) && { items: [] }),
        })
      }
      return updatedSelected
    }

    executeOptimisticContinuar({
      commitUi: () => {
        const updatedSelected = buildUpdatedSelected()
        // seat:selected already sent on tap — do not push again
        clearRemoteSeatType(seat.seatId)
        currentSelected.current = updatedSelected
        setSelected(updatedSelected)
        destroyModalView()
        if (!needsFareBlock && tracker.isTracking(seat.seatId)) {
          tracker.reset()
        }
      },
      needsFareBlock,
      blockFare: () => blockSeatWithFare(seatWithSync, fareId, syncEntryId),
      rollback: () => {
        // Mapped alert already shown by blockSeatWithFare
        rollbackContinuarSelection(seatWithSync, fareId)
      },
      onSettled: () => {
        if (tracker.isTracking(seat.seatId)) {
          tracker.reset()
        }
      },
    })
  }

  const handleCreateCart = () => {
    setLocalLoading(true)

    // Si es un viaje redondo y no hay datos en `bookingReturningTrip`, regresar a la página de selección de asientos
    if (isRoundTripDeparture(bookingTripConfig, bookingReturningTrip)) {
      console.log(
        "isRoundTripDeparture: ",
        isRoundTripDeparture(bookingTripConfig, bookingReturningTrip),
      )
      navigate(`${BASE_URL}/booking/trips`)
      return
    }

    const seatsMissingSync = (selected || []).flatMap((passenger) =>
      (passenger.seats || []).filter(
        (s) => s.syncEntryId == null || s.syncEntryId === "",
      ),
    )
    if (seatsMissingSync.length > 0) {
      console.warn(
        "[BookingBus] cart blocked: seat(s) missing syncEntryId",
        seatsMissingSync.map((s) => ({ type: s.type, seatId: s.seatId })),
      )
      setLocalLoading(false)
      showErrorAlert(
        "Aún se están reservando los asientos. Espera un momento e intenta de nuevo.",
        { setupModal, showModal, hideModal },
        { title: "Un momento" },
      )
      return
    }

    const cartList = {
      departure_id: bookingDepartureTrip.id,
      ...(isRoundTripReturn(bookingTripConfig, bookingReturningTrip) &&
      bookingReturningTrip?.id
        ? { return_id: bookingReturningTrip.id }
        : {}),
      passengers: selected,
    }

    transpais
      .cart(session, cartList)
      .then((response) => {
        setLocalLoading(false)
        console.log("response cart: ", response)
        setBookingDepartureBlockedTickets(response.data)
      })
      .catch((error) => {
        const { userMessage, userTitle, errorData } = handleApiError(error, {
          selectedSeats: selected,
          tripId: bookingDepartureTrip.id,
        })
        showErrorAlert(userMessage, { setupModal, showModal, hideModal }, {
          title: userTitle,
        })
        reportError(error, errorData)
        setLocalLoading(false)
      })
  }

  const handleBackButtonClick = () => {
    // Verificar si estamos seleccionando asientos de regreso
    if (!isEmpty(bookingReturningTrip)) {
      console.log(
        'Seleccionando asientos de regreso. Deseleccionando asientos de tipo "return".',
      )

      // Iterar sobre los pasajeros y deseleccionar los asientos de tipo "return"
      const updatedSelected = selected
        .map((passenger) => {
          const returnSeats = passenger.seats.filter(
            (seat) => seat.type === "return",
          )

          // Enviar "seat:unselected" para cada asiento de tipo "return"
          returnSeats.forEach((seat) => {
            if (channelRef.current) {
              const segment = bookingReturningTrip.segments[0]
              pushSeatUnselected(channelRef.current, seat, segment)
            }
            void softReleaseSeatHttp(seat, passenger.fareId ?? null)
          })

          // Filtrar los asientos que no son de tipo "return"
          const filteredSeats = passenger.seats.filter(
            (seat) => seat.type !== "return",
          )

          // Si el pasajero no tiene más asientos, no lo incluimos en el resultado
          if (filteredSeats.length === 0) {
            return null
          }

          // Retornar el pasajero con los asientos filtrados
          return {
            ...passenger,
            seats: filteredSeats,
          }
        })
        .filter(Boolean) // Eliminar pasajeros nulos

      // Actualizar la variable `selected`
      setSelected(updatedSelected)

      // Limpiar `bookingReturningTrip`
      setBookingReturningTrip(null)
    } else if (!isEmpty(bookingDepartureTrip)) {
      console.log(
        "Seleccionando asientos de ida. Deseleccionando todos los asientos.",
      )

      // Iterar sobre los pasajeros y deseleccionar todos los asientos
      selected.forEach((passenger) => {
        passenger.seats.forEach((seat) => {
          if (channelRef.current) {
            const segment = bookingDepartureTrip.segments[0]
            pushSeatUnselected(channelRef.current, seat, segment)
          }
          void softReleaseSeatHttp(seat, passenger.fareId ?? null)
        })
      })

      // Limpiar la variable `selected`
      setSelected([])

      // Limpiar `bookingDepartureTrip`
      setBookingDepartureTrip(null)
    }

    // Navegar a la página anterior
    console.log("Navegando a la página de selección de viajes.")
    navigate(`${BASE_URL}/booking/trips`)
  }

  const setupSocket = () => {
    if (!accessTicket || !bookingDepartureTrip) return

    console.log("Inicializando conexión WebSocket...")
    setRetryCount(0) // Resetear contador de reintentos

    // Configuración del Socket
    if (!socketRef.current) {
      const socketUrl = process.env.REACT_APP_WSS + "/seatmaps/socket"

      socketRef.current = new Socket(socketUrl, {
        params: { token: accessTicket },
        logger: (kind, msg, data) => {
          console.log(`Socket ${kind}: ${msg}`, data)
        },
        reconnectAfterMs: (tries) => {
          const delay =
            RETRY_DELAYS[tries - 1] || RETRY_DELAYS[RETRY_DELAYS.length - 1]
          console.log(`Socket will attempt to reconnect in ${delay}ms`)
          return delay
        },
      })

      // Configurar event listeners
      socketRef.current.onOpen(() => {
        console.log("Socket conectado exitosamente")
        setIsSocketConnected(true)
        setRetryCount(0) // Resetear contador al conectar
        if (isFlightRecorderEnabled()) {
          recordFlightEvent("WS:SOCKET_OPEN", { topic: "seatmaps" }, "BookingBus.setupSocket")
        }
        setupChannel()
      })

      socketRef.current.onError((error) => {
        console.error("Error en socket:", error)
        setIsSocketConnected(false)
        if (isFlightRecorderEnabled()) {
          recordFlightEvent(
            "WS:SOCKET_ERROR",
            { message: error != null ? String(error).slice(0, 160) : null },
            "BookingBus.setupSocket",
          )
        }

        if (
          error.toString().includes("403") ||
          error.toString().includes("unauthorized")
        ) {
          console.error(
            "ERROR: Token inválido o expirado. Necesitas obtener un nuevo accessTicket",
          )
          // No intentar reconectar para errores de autenticación
          //return
        }

        // Intentar reconexión para otros errores
        //reconnectSocket()
      })

      socketRef.current.onClose((event) => {
        console.log("Socket cerrado:", event)
        setIsSocketConnected(false)
        if (isFlightRecorderEnabled()) {
          recordFlightEvent(
            "WS:SOCKET_CLOSE",
            { code: event?.code, reason: event?.reason ? String(event.reason).slice(0, 120) : null },
            "BookingBus.setupSocket",
          )
        }
        // Intentar reconexión
        //reconnectSocket()
      })

      socketRef.current.connect()
    } else if (!isSocketConnected) {
      // Si el socket existe pero no está conectado, intentar reconectar
      //reconnectSocket()
    }
  }

  // Configuración del canal (mejorada)
  const setupChannel = () => {
    //if (!socketRef.current || !bookingDepartureTrip) return

    const segment = bookingDepartureTrip.segments[0]
    const channelName = `seatmap:${segment.routeId}_${segment.name}_${segment.manifestDate}`

    // Solo crear nuevo canal si no existe o si los parámetros cambiaron
    // if (!channelRef.current || channelRef.current.topic !== channelName) {
    //   if (channelRef.current) {
    //     channelRef.current.leave()
    //   }

    const newChannel = socketRef.current.channel(channelName, {
      leg_from: segment.legFrom.toString(),
      leg_to: segment.legTo.toString(),
      seat_map_id: segment.seatMapId,
    })

    // Configurar listeners del canal
    newChannel.on("seat:selected", (msg) => {
      const seatId = msg.selected_seat.seat_id
      if (isFlightRecorderEnabled()) {
        recordFlightEvent(
          "WS:IN",
          { event: "seat:selected", direction: "in", seatId, topic: channelName },
          "BookingBus.channel",
        )
      }
      if (shouldPaintOccupied(seatId, ownershipContext())) {
        updateSeatStatus(seatId, "occupied")
      }
    })

    newChannel.on("seat:unselected", (msg) => {
      const seatId = msg.unselected_seat.seat_id
      if (isFlightRecorderEnabled()) {
        recordFlightEvent(
          "WS:IN",
          { event: "seat:unselected", direction: "in", seatId, topic: channelName },
          "BookingBus.channel",
        )
      }
      clearRemoteSeatType(seatId)
    })

    newChannel.on("sync:join", (msg) => {
      if (isFlightRecorderEnabled()) {
        const n = Array.isArray(msg?.seats) ? msg.seats.length : null
        recordFlightEvent(
          "WS:IN",
          { event: "sync:join", direction: "in", topic: channelName, seatCount: n },
          "BookingBus.channel",
        )
      }
      setSyncSeats(msg.seats)
    })

    // Unirse al canal
    newChannel
      .join()
      .receive("ok", (response) => {
        console.log("Canal unido exitosamente", response)
        channelRef.current = newChannel
        if (isFlightRecorderEnabled()) {
          recordFlightEvent(
            "WS:CHANNEL_JOIN_OK",
            { topic: channelName },
            "BookingBus.setupChannel",
          )
        }
      })
      .receive("error", ({ reason }) => {
        console.error("Error al unirse al canal:", reason)
        if (isFlightRecorderEnabled()) {
          recordFlightEvent(
            "WS:CHANNEL_JOIN_ERROR",
            { topic: channelName, reason: reason != null ? String(reason).slice(0, 160) : null },
            "BookingBus.setupChannel",
          )
        }
        if (reason.includes("unauthorized") || reason.includes("invalid")) {
          console.error("ERROR CRÍTICO: Verifica tu token de acceso")
        } else {
          // Reintentar unión al canal después de un delay
          setTimeout(setupChannel, 2000)
        }
      })
    //}
  }

  useEffect(() => {
    if (accessTicket) {
      setupSocket()
    }

    return () => {
      // Limpieza
      if (channelRef.current) {
        console.log(
          "Saliendo del canal WebSocket...+++++++++++++++++++++++++++++++++++++++++++++++++++++",
        )
        channelRef.current.leave()
        channelRef.current = null
      }
      if (socketRef.current) {
        console.log(
          "Desconectando el socket WebSocket...------------------------------------------------",
        )
        socketRef.current.disconnect()
        socketRef.current = null
      }
    }
  }, [accessTicket])

  useEffect(() => {
    // Redirigir a la revisión cuando el carrito esté listo y sea un viaje sencillo
    if (
      isSingleTrip(bookingTripConfig) &&
      !isEmpty(bookingDepartureBlockedTickets)
    ) {
      navigate(`${BASE_URL}/booking/review`)
    } else if (
      isRoundTripReturn(bookingTripConfig, bookingReturningTrip) &&
      !isEmpty(bookingDepartureBlockedTickets)
    ) {
      navigate(`${BASE_URL}/booking/review`)
    }
  }, [bookingDepartureBlockedTickets, bookingTripConfig])

  useEffect(() => {
    if (bookingDepartureTrip && bookingOrigin && bookingDestination) {
      if (isSingleTrip(bookingTripConfig)) {
        fetchBusData(
          session,
          bookingDepartureTrip,
          bookingOrigin,
          bookingDestination,
          isRoundTripReturn(bookingTripConfig, bookingReturningTrip),
        )
      }

      // Si estamos seleccionando los asientos de ida
      if (isRoundTripDeparture(bookingTripConfig, bookingReturningTrip)) {
        fetchBusData(
          session,
          bookingDepartureTrip,
          bookingOrigin,
          bookingDestination,
          false, // No es viaje de regreso
        )
      }

      // Si estamos seleccionando los asientos de regreso
      if (isRoundTripReturn(bookingTripConfig, bookingReturningTrip)) {
        fetchBusData(
          session,
          bookingReturningTrip,
          bookingDestination,
          bookingOrigin,
          true, // Es viaje de regreso
        )
      }
    }
  }, [bookingDepartureTrip, bookingOrigin, bookingDestination])

  useEffect(() => {
    setDisabled(!verifyCountFares(countFares, selected))
  }, [selected])

  useEffect(() => {
    const fetchAccessTicket = async () => {
      try {
        const response = await transpais.seatLive(session, {})
        setAccessTicket(response.data.ticket)
      } catch (error) {
        console.error("Error fetching bus data:", error)
        Sentry.captureException(error)
      }
    }
    fetchAccessTicket()
  }, [])

  useEffect(() => {
    if (syncSeats.length > 0) {
      const remoteIds = remoteOccupiedSeatIds(syncSeats, ownershipContext())
      remoteIds.forEach((seatId) => {
        console.log("Asiento ocupado:", seatId)
        updateSeatStatus(seatId, "occupied")
      })
    }
  }, [syncSeats])

  const didMount = useRef(true)

  useEffect(() => {
    // Solo ejecutar la limpieza de selected en el primer render (posible recarga)
    if (
      didMount.current &&
      // channelRef.current &&
      selected &&
      selected.length > 0
    ) {
      if (isRoundTripReturn(bookingTripConfig, bookingReturningTrip)) {
        console.log("Limpiando asientos de regreso en selected")
        const updatedSelected = selected
          .map((passenger) => {
            const filteredSeats = passenger.seats.filter(
              (seat) => seat.type !== "return",
            )
            if (filteredSeats.length === 0) return null
            return { ...passenger, seats: filteredSeats }
          })
          .filter(Boolean)
        setSelected(updatedSelected)
      }
      if (
        isSingleTrip(bookingTripConfig) ||
        isRoundTripDeparture(bookingTripConfig, bookingReturningTrip)
      ) {
        console.log("Limpiando todos los asientos en selected")
        setSelected([])
      }
    }
    didMount.current = false
    // eslint-disable-next-line
  }, [
    channelRef.current,
    bookingTripConfig,
    bookingReturningTrip,
    bookingDepartureTrip,
  ])

  const onTimeout = () => {
    if (didRunTimeout.current) return
    didRunTimeout.current = true
    handleBookingTimeout({
      hideTimer,
      setDeadline: setBookingTimeoutDeadline,
      setupModal,
      showModal,
      navigate,
      hideModal,
      message:
        "Lo sentimos 😓. El tiempo para seleccionar tus asientos terminó. Inicia nuevamente tu compra.",
    })
  }

  useEffect(() => {
    if (bookingTimeoutDeadline) {
      const diff = differenceInSeconds(
        parseISO(bookingTimeoutDeadline),
        new Date(),
      )
      if (diff <= 0 && !didRunTimeout.current) {
        onTimeout()
      }
    } else {
      didRunTimeout.current = false
    }
  }, [bookingTimeoutDeadline])

  return (
    <>
      <Loader show={localLoading} />
      <section className={["section-full", "section-flex"].join(" ")}>
        <div className={[global.top, styles.top].join(" ")}>
          <div
            className={[
              "logo",
              bookingTripConfig.redondo ? "roundAlign" : null,
              styles.logo,
            ].join(" ")}
          >
            <figure
              className={[
                "logo-image",
                "logo-image-transpais-light",
                bookingTripConfig.redondo ? "logo-small" : null,
              ].join(" ")}
            />
            {!isSingleTrip(bookingTripConfig) && (
              <div className={styles.toPill}>
                <div className={styles.pill}>
                  <p>
                    {isRoundTripDeparture(
                      bookingTripConfig,
                      bookingReturningTrip,
                    )
                      ? "Selecciona tus asientos de ida"
                      : "Selecciona tus asientos de regreso"}
                  </p>
                </div>
              </div>
            )}
            <div className={styles.shoppCart}>
              <Timer variant={"clock"} />
            </div>
          </div>

          <div className={styles.middle}>
            <div className={global.content}>
              <div className={styles.tripsHeader}>
                <TripData
                  variant={"brief"}
                  data={{
                    origin: bookingOrigin,
                    destination: bookingDestination,
                    ...(isSingleTrip(bookingTripConfig)
                      ? {}
                      : {
                          returning: !isRoundTripDeparture(
                            bookingTripConfig,
                            bookingReturningTrip,
                          ),
                        }),
                  }}
                />
                <TripData
                  variant={"meta"}
                  meta={{
                    departure: bookingDepartureTrip,
                    returning: bookingReturningTrip,
                  }}
                />
              </div>

              <hr />

              <div className={styles.departures}>
                <div className={styles.layout}>
                  <div className={styles.acotaciones}>
                    <Acotaciones items={[]} />
                  </div>

                  <div className={styles.bus}>
                    <BusLayout
                      layout={bus}
                      disponibilidad={
                        bookingTripConfig.redondo
                          ? bookingReturningDisponibilidad
                          : bookingDepartureDisponibilidad
                      }
                      selected={selected}
                      onSelect={handleSelectSeat}
                      selectFloor={setSelectedFloor}
                      tripType={bookingTripConfig.type} // Pasar el tipo de viaje
                      isSelectingReturn={isRoundTripReturn(
                        bookingTripConfig,
                        bookingReturningTrip,
                      )} // Indicar si estamos seleccionando el viaje de regreso
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <footer className={global.bottom}>
          <div>
            <Button
              onClick={handleBackButtonClick}
              type="neutral"
              left-icon="chevron-left"
            >
              Volver
            </Button>
          </div>
          <div className={global.middle}></div>
          <div>
            <Button
              disabled={disabled}
              onClick={handleCreateCart}
              type="positive"
              right-icon="chevron-right"
            >
              Continuar
            </Button>
          </div>
        </footer>
      </section>
    </>
  )
}

export default BookingBus
